import React, { useState, useEffect } from 'react';
import {
  FaSearch,
  FaChartBar,
  FaChartLine,
  FaChartPie,
  FaChartArea,
  FaTable,
  FaPlus,
  FaArrowLeft,
  FaArrowRight,
  FaEye,
  FaCog,
  FaFilter,
  FaRefresh,
  FaTachometerAlt,
  FaTasks
} from 'react-icons/fa';
import DataApi from '../../api/DataApi';
import { ChartDisplay } from '../reports/charts';
import './css/HomeWidgetBuilder.css';

const HomeWidgetBuilder = ({
  isOpen,
  onClose,
  onAddWidget,
  onPreviewWidget,
  editingWidget,
  existingWidgets = []
}) => {
  const rowOptions = Array.from({ length: 25 }, (_, index) => index + 1);
  const getRowOptionLabel = (value) => {
    if (value === 1) return '1 Row';
    if (value === 4) return '4 Rows (Default)';
    return `${value} Rows`;
  };
  const [currentStep, setCurrentStep] = useState(1);
  const [widgetType, setWidgetType] = useState(null); // 'dashboard' or 'task'
  const [dashboards, setDashboards] = useState([]);
  const [filteredDashboards, setFilteredDashboards] = useState([]);
  const [selectedDashboard, setSelectedDashboard] = useState(null);
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportCharts, setReportCharts] = useState([]);
  const [selectedChart, setSelectedChart] = useState(null);
  const [includeFullReport, setIncludeFullReport] = useState(false);
  // Report data processing states (following ReportDataViewer pattern)
  const [reportInfo, setReportInfo] = useState(null);
  const [selectedFields, setSelectedFields] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [summarizedFields, setSummarizedFields] = useState({});
  const [previewData, setPreviewData] = useState([]);
  const [groupingInfo, setGroupingInfo] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [widgetConfig, setWidgetConfig] = useState({
    name: '',
    widget_type: '', // 'dashboard' or 'task'
    chart_type: 'bar',
    column_span: 6, // Default to half of 12-column grid
    row_span: 4, // Default height
    refresh_rate: 300, // 5 minutes
    show_filters: true,
    show_export: true,
    show_header: true, // Control whether to show dashboard/component header
    sort_order: '', // Sort order number
    chart_config: {},
    dashboard_id: null, // For dashboard widgets
    task_id: null // For task widgets
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {

      if (editingWidget) {
        console.log('WidgetBuilder - Editing widget:', editingWidget);
        // Initialize form for editing
        const rawWidgetType = editingWidget.widget_type || editingWidget.type || 'dashboard';
        const widgetType = rawWidgetType === 'report' ? 'report' : rawWidgetType === 'chart' ? 'report' : rawWidgetType;
        setWidgetType(widgetType);

        const isFullReportWidget = rawWidgetType === 'report' && (!editingWidget.chart_id && !editingWidget.chart_config);
        setIncludeFullReport(isFullReportWidget);

        // Set the correct step based on widget type
        if (widgetType === 'dashboard') {
          setCurrentStep(3); // Dashboard goes to step 3 (config)
        } else {
          setCurrentStep(4); // Task goes to step 4 (config)
        }
        setWidgetConfig({
          name: editingWidget.name || '',
          widget_type: isFullReportWidget ? 'report' : (editingWidget.widget_type || editingWidget.type || 'dashboard'),
          chart_type: editingWidget.chart_type || (isFullReportWidget ? 'report' : 'bar'),
          column_span: editingWidget.column_span || 6,
          row_span: editingWidget.row_span || 4,
          refresh_rate: editingWidget.refresh_rate || 300,
          show_filters: editingWidget.show_filters !== undefined ? editingWidget.show_filters : true,
          show_export: editingWidget.show_export !== undefined ? editingWidget.show_export : true,
          show_header: editingWidget.show_header !== undefined ? editingWidget.show_header : true,
          sort_order: editingWidget.sort_order || '',
          chart_config: isFullReportWidget ? {} : (editingWidget.chart_config || {}),
          dashboard_id: editingWidget.dashboard_id || editingWidget.parent_id || null,
          task_id: editingWidget.task_id || null
        });

        if (isFullReportWidget) {
          setSelectedChart(null);
        } else {
          const chartData = editingWidget.chart_config || editingWidget.selectedChart || {};
          setSelectedChart(chartData && chartData.id ? chartData : null);
        }
        console.log('WidgetBuilder - Editing widget chart data:', {
          hasSelectedChart: !!editingWidget.selectedChart,
          hasChartConfig: !!editingWidget.chart_config,
          chartConfig: editingWidget.chart_config
        });

        // Set selected dashboard or report for editing
        if (widgetType === 'dashboard') {
          // For dashboard widgets, set the selected dashboard
          setSelectedDashboard({
            id: editingWidget.dashboard_id || editingWidget.parent_id,
            name: editingWidget.name?.split(' - ')[0] || 'Unknown Dashboard'
          });
        } else {
          // For task widgets, set the selected chart and report
          const chartData = editingWidget.chart_config || editingWidget.selectedChart || {};
          setSelectedChart(chartData);
          setSelectedReport({
            id: editingWidget.report_id,
            name: editingWidget.name?.split(' - ')[0] || 'Unknown Report'
          });
        }
      } else {
        resetBuilder();
      }
    }
  }, [isOpen, editingWidget]);

  useEffect(() => {
    filterReports();
  }, [reports, searchTerm]);

  useEffect(() => {
    filterDashboards();
  }, [dashboards, searchTerm]);

  const resetBuilder = () => {
    setCurrentStep(1);
    setWidgetType(null);
    setSelectedDashboard(null);
    setSelectedReport(null);
    setSelectedChart(null);
    setIncludeFullReport(false);
    setSearchTerm('');
    setSelectedFields([]);
    setSummarizedFields({});
    setSelectedGroups([]);
    setPreviewData([]);
    setGroupingInfo(null);
    setWidgetConfig({
      name: '',
      widget_type: '',
      chart_type: 'bar',
      column_span: 6, // Default to half of 12-column grid
      row_span: 4, // Default height
      refresh_rate: 300,
      show_filters: true,
      show_export: true,
      show_header: true,
      chart_config: {},
      dashboard_id: null,
      task_id: null
    });
  };

  const loadDashboards = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Loading dashboards from API...');
      const dashboardApi = new DataApi('dashboard');
      const response = await dashboardApi.fetchAll();

      console.log('Dashboards API response:', response);

      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        setDashboards(response.data.data);
        console.log('Dashboards loaded successfully:', response.data.data.length, 'dashboards');
      } else {
        setError('Failed to load dashboards');
        console.error('API returned invalid data:', response);
      }
    } catch (err) {
      console.error('Error loading dashboards:', err);
      setError('Error loading dashboards. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadReports = async () => {
    try {
      setLoading(true);
      setError(null);
      const reportApi = new DataApi('reports'); // Fixed: use 'reports' (plural) to match backend route
      console.log('Loading reports from API...');
      const response = await reportApi.fetchAll();

      console.log('Reports API response:', response.data);

      if (response.data.success) {
        setReports(response.data.data);
        console.log('Reports loaded successfully:', response.data.data.length, 'reports');
      } else {
        setError('Failed to load reports');
        console.error('API returned success: false', response.data);
      }
    } catch (err) {
      console.error('Error loading reports:', err);
      setError('Error loading reports. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filterDashboards = () => {
    if (!searchTerm.trim()) {
      setFilteredDashboards(dashboards);
    } else {
      const filtered = dashboards.filter(dashboard =>
        dashboard.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (dashboard.description && dashboard.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredDashboards(filtered);
    }
  };

  const filterReports = () => {
    if (!searchTerm.trim()) {
      setFilteredReports(reports);
    } else {
      const filtered = reports.filter(report =>
        report.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (report.description && report.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredReports(filtered);
    }
  };


  // Function to get the next available sort order
  const getNextSortOrder = () => {
    if (!existingWidgets || existingWidgets.length === 0) {
      return 0; // First widget starts at 0
    }

    // Find the maximum sort order from existing widgets
    const maxSortOrder = Math.max(...existingWidgets.map(widget => widget.sort_order || 0));

    // Return the next available sort order
    return maxSortOrder + 1;
  };

  const handleTypeSelect = (type) => {
    console.log('Widget type selected:', type);
    setWidgetType(type);

    // Calculate next sort order
    const nextSortOrder = getNextSortOrder();

    setWidgetConfig(prev => ({
      ...prev,
      widget_type: type === 'report' ? 'chart' : type,
      sort_order: nextSortOrder
    }));

    if (type === 'dashboard') {
      loadDashboards();
      setCurrentStep(2);
    } else if (type === 'report') {
      loadReports();
      setIncludeFullReport(false);
      setSelectedChart(null);
      setCurrentStep(2);
    } else if (type === 'task') {
      loadReports();
      setIncludeFullReport(false);
      setCurrentStep(2);
    }

  };

  const handleDashboardSelect = (dashboard) => {
    console.log('Dashboard selected:', dashboard);
    setSelectedDashboard(dashboard);
    setWidgetConfig(prev => ({
      ...prev,
      name: `${dashboard.name} Widget`,
      dashboard_id: dashboard.id,
      sort_order: prev.sort_order // Preserve the sort order set in handleTypeSelect
    }));

    setCurrentStep(3); // Go to widget configuration
  };

  const handleReportSelect = async (report) => {
    console.log('Report selected:', report);
    setSelectedReport(report);
    setWidgetConfig(prev => ({
      ...prev,
      name: `${report.report_label} Widget`,
      report_label: report.report_label,  // Save report label for later use
      sort_order: prev.sort_order // Preserve the sort order set in handleTypeSelect
    }));
    setIncludeFullReport(false);
    setSelectedChart(null);

    try {
      // Load full report data following ReportDataViewer pattern
      await loadReportData(report.id);
    } catch (err) {
      console.error('Error loading report data:', err);
      setError('Error loading report data. Please try again.');
    }

    setCurrentStep(3);
  };

  const handleIncludeFullReportToggle = (checked) => {
    setIncludeFullReport(checked);
    setSelectedChart(null);
    setWidgetConfig(prev => ({
      ...prev,
      widget_type: checked ? 'report' : 'chart',
      chart_type: checked ? 'report' : prev.chart_type,
      chart_config: checked ? {} : prev.chart_config
    }));
  };

  // Load report data following ReportDataViewer pattern
  const loadReportData = async (reportId) => {
    setPreviewLoading(true);
    setError(null);

    try {
      // First, get the report configuration (same as ReportDataViewer)
      const reportBuilderApi = new DataApi('reportbuilder');
      const reportResponse = await reportBuilderApi.get(`/${reportId}`);

      if (!reportResponse?.data?.success) {
        throw new Error('Failed to load report configuration');
      }

      const report = reportResponse.data.data;
      console.log('WidgetBuilder - Loaded report for viewer:', report);

      // Set report information (same as ReportDataViewer)
      setReportInfo({
        id: report.id,
        name: report.name,
        report_label: report.report_label,
        description: report.description,
        object_name: report.object_name,
        object_label: report.object_label,
        report_type: report.report_type,
        is_public: report.is_public,
        filter_logic: report.filter_logic,
        filter_custom_logic: report.filter_custom_logic,
        report_objects: report.report_objects || []
      });

      // Process report fields and groups (same as ReportDataViewer)
      if (report.fields && report.fields.length > 0) {
        const fields = report.fields.map(field => ({
          id: field.field_config_id,
          field_name: field.field_name,
          field_label: field.field_label,
          data_type: field.data_type,
          object_name: field.object_name,
          is_aggregated: field.is_aggregated,
          aggregate_function: field.aggregate_function,
          is_grouped: field.is_grouped,
          sort_order: field.sort_order
        }));

        // Helper function to convert database aggregation values to dropdown values
        const convertAggregationValue = (dbValue) => {
          const mapping = {
            'average': 'avg',
            'sum': 'sum',
            'count': 'count',
            'min': 'min',
            'max': 'max'
          };
          return mapping[dbValue] || dbValue;
        };

        // Process fields and create summarized fields
        const selectedFieldsList = [];
        const groups = [];
        const summarizedFieldsData = {};

        fields.forEach(field => {
          selectedFieldsList.push(field);

          if (field.is_grouped) {
            groups.push(field);
          }

          if (field.is_aggregated && field.aggregate_function) {
            summarizedFieldsData[field.id] = {
              aggregation: convertAggregationValue(field.aggregate_function)
            };
          }
        });

        setSelectedFields(selectedFieldsList);
        setSelectedGroups(groups);
        setSummarizedFields(summarizedFieldsData);

        // Load charts from the report data (same as ReportDataViewer)
        console.log('WidgetBuilder - Report charts data:', report.charts);
        console.log('WidgetBuilder - Is charts array?', Array.isArray(report.charts));
        console.log('WidgetBuilder - Charts length:', report.charts ? report.charts.length : 'undefined');

        if (report.charts && Array.isArray(report.charts) && report.charts.length > 0) {
          console.log('WidgetBuilder - Setting charts from report data:', report.charts);
          setReportCharts(report.charts);
        } else {
          console.log('WidgetBuilder - No charts found in report data');
          setReportCharts([]);
        }
      }

      // Fetch actual report data for charts (same as ReportDataViewer)
      await fetchReportData(report);

    } catch (error) {
      console.error('Error loading report data:', error);
      setError('Error loading report data. Please try again.');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Fetch actual report data (same as ReportDataViewer)
  const fetchReportData = async (report, filters = {}) => {
    setPreviewLoading(true);
    console.log('WidgetBuilder - fetchReportData:', {
      reportId: report.id,
      limit: 10, // Limit to 10 records for widget preview
      filters: filters
    });

    try {
      // Use the same route as ReportDataViewer to fetch actual report data
      const dataApi = new DataApi('object');
      const response = await dataApi.post('/view/report', {
        reportId: report.id,
        limit: 10000, // Limit to 10 records for widget preview
        filters: filters
      });

      if (response?.data?.success) {
        const rawData = response.data.data;
        const grouping = response.data.grouping;

        console.log('WidgetBuilder - Report data loaded:', rawData);
        console.log('WidgetBuilder - Grouping info:', grouping);

        setPreviewData(rawData);
        setGroupingInfo(grouping);

      } else {
        throw new Error(response?.data?.message || 'Failed to fetch report data');
      }

    } catch (err) {
      console.error('WidgetBuilder - Error fetching report data:', err);
      setError('Error fetching report data. Please try again.');
    } finally {
      setPreviewLoading(false);
    }
  };


  // Create summarized data for charts (same as dashboard WidgetBuilder)
  const createSummarizedDataForCharts = () => {
    if (!groupingInfo || !groupingInfo.hasGrouping || !previewData || previewData.length === 0) {
      return {};
    }

    const summarizedData = {};
    const groupedFields = groupingInfo.groupedFields;
    const nonGroupedFields = selectedFields.filter(field =>
      !groupedFields.some(groupField =>
        groupField.field_name === field.field_name &&
        groupField.object_name === field.object_name
      )
    );

    // Initialize metadata
    summarizedData._metadata = {
      group_fields: {},
      summarized_fields: {},
      report_info: {
        total_records: previewData.length,
        group_count: groupedFields.length,
        summarized_field_count: Object.keys(summarizedFields).length,
        extraction_timestamp: new Date().toISOString(),
        data_structure_version: '2.0'
      }
    };

    // Add metadata for grouped fields
    groupedFields.forEach(field => {
      summarizedData._metadata.group_fields[field.field_name] = {
        field_id: field.id,
        field_name: field.field_name,
        field_label: field.field_label,
        data_type: field.data_type,
        object_name: field.object_name,
        field_type: field.field_type
      };
    });

    // Add metadata for summarized fields
    nonGroupedFields.forEach(field => {
      const summarizedField = summarizedFields[field.id];
      if (summarizedField) {
        summarizedData._metadata.summarized_fields[field.field_name] = {
          field_id: field.id,
          field_name: field.field_name,
          field_label: field.field_label,
          data_type: field.data_type,
          object_name: field.object_name,
          aggregation: summarizedField.aggregation
        };
      }
    });

    // Process all grouped fields, not just the first one
    if (groupedFields.length > 0) {
      // Process each grouped field to create data structures for all possible xAxisFields
      groupedFields.forEach(primaryGroupField => {
        const groupFieldName = primaryGroupField.field_name;

        // Get unique group values for this specific field
        const groupValues = new Set();
        previewData.forEach(row => {
          const tableName = primaryGroupField.object_name || 'main';
          const aliasedFieldName = `${tableName}_${primaryGroupField.field_name}`;
          const fieldData = row[aliasedFieldName] || row[primaryGroupField.field_name];

          let groupValue;
          if (typeof fieldData === 'object' && fieldData !== null) {
            groupValue = fieldData.value || fieldData.displayValue;
          } else {
            groupValue = fieldData;
          }

          if (groupValue !== null && groupValue !== undefined && groupValue !== '') {
            groupValues.add(groupValue);
          }
        });

        // Initialize each summarized field in the root level (only once)
        if (groupFieldName === groupedFields[0].field_name) {
          nonGroupedFields.forEach(field => {
            const summarizedField = summarizedFields[field.id];
            if (summarizedField) {
              if (!summarizedData[field.field_name]) {
                summarizedData[field.field_name] = {};
              }
            }
          });
        }

        // Calculate aggregated values for each group value
        groupValues.forEach(groupValue => {
          const rowsForGroup = previewData.filter(row => {
            const tableName = primaryGroupField.object_name || 'main';
            const aliasedFieldName = `${tableName}_${primaryGroupField.field_name}`;
            const fieldData = row[aliasedFieldName] || row[primaryGroupField.field_name];

            let rowGroupValue;
            if (typeof fieldData === 'object' && fieldData !== null) {
              rowGroupValue = fieldData.value || fieldData.displayValue;
            } else {
              rowGroupValue = fieldData;
            }

            return rowGroupValue === groupValue;
          });

          // Calculate aggregated values for each non-grouped field
          nonGroupedFields.forEach(field => {
            const summarizedField = summarizedFields[field.id];
            if (summarizedField) {
              const aggregation = summarizedField.aggregation;
              const aggregatedValue = calculateAggregation(rowsForGroup, field, aggregation);

              // For charts, we need raw numeric values, not formatted strings
              let chartValue = aggregatedValue;
              if (field.data_type === 'currency' && typeof aggregatedValue === 'string') {
                // Extract numeric value from currency string (e.g., "₹35,000.00" -> 35000)
                const numericValue = parseFloat(aggregatedValue.replace(/[₹,]/g, ''));
                chartValue = isNaN(numericValue) ? 0 : numericValue;
                console.log(`HomeWidgetBuilder - Currency conversion: "${aggregatedValue}" -> ${chartValue}`);
              } else if (field.data_type === 'number' || field.data_type === 'decimal') {
                // Ensure numeric values are properly parsed
                const numericValue = parseFloat(aggregatedValue);
                chartValue = isNaN(numericValue) ? 0 : numericValue;
                console.log(`HomeWidgetBuilder - Numeric conversion: "${aggregatedValue}" -> ${chartValue}`);
              }

              // Structure: { "salary": { "Store": 35000, "FrontDesk": 203000 } }
              summarizedData[field.field_name][groupValue] = chartValue;
            }
          });
        });

        // Create the main structure that ChartDisplay expects for this group field
        summarizedData[groupFieldName] = {};
        groupValues.forEach(groupValue => {
          summarizedData[groupFieldName][groupValue] = {};
          nonGroupedFields.forEach(field => {
            const summarizedField = summarizedFields[field.id];
            if (summarizedField && summarizedData[field.field_name][groupValue] !== undefined) {
              // ChartDisplay expects: { "salary": { "value": 35000 } }
              summarizedData[groupFieldName][groupValue][field.field_name] = {
                value: summarizedData[field.field_name][groupValue]
              };
            }
          });
        });
      });
    }

    console.log('HomeWidgetBuilder - Created summarized data for charts:', summarizedData);
    return summarizedData;
  };

  // Helper function to parse currency and numeric values
  const parseNumericValue = (rawValue, dataType) => {
    if (rawValue === null || rawValue === undefined || rawValue === '') {
      return 0;
    }

    // Handle currency values (e.g., "₹35,000.00" -> 35000)
    if (dataType === 'currency' && typeof rawValue === 'string') {
      // Extract numeric value from currency string - remove ₹, $, commas
      const numericValue = parseFloat(rawValue.replace(/[₹$,]/g, ''));
      console.log(`HomeWidgetBuilder - Currency conversion: "${rawValue}" -> ${numericValue}`);
      return isNaN(numericValue) ? 0 : numericValue;
    } else if (dataType === 'number' || dataType === 'decimal') {
      // Handle regular numeric values
      const numericValue = parseFloat(rawValue);
      console.log(`HomeWidgetBuilder - Numeric conversion: "${rawValue}" -> ${numericValue}`);
      return isNaN(numericValue) ? 0 : numericValue;
    } else {
      // Fallback for other data types
      return parseFloat(rawValue) || 0;
    }
  };

  // Calculate aggregation (same as dashboard WidgetBuilder with currency support)
  const calculateAggregation = (rows, field, aggregation) => {
    if (!rows || rows.length === 0) return 0;

    const tableName = field.object_name || 'main';
    const aliasedFieldName = `${tableName}_${field.field_name}`;

    const values = rows.map(row => {
      const fieldData = row[aliasedFieldName] || row[field.field_name];
      if (fieldData) {
        let rawValue;
        if (typeof fieldData === 'object' && fieldData !== null) {
          rawValue = fieldData.value;
        } else {
          rawValue = fieldData;
        }

        // Use the helper function to parse numeric values
        return parseNumericValue(rawValue, field.data_type);
      }
      return 0;
    }).filter(val => !isNaN(val));

    if (values.length === 0) return 0;

    switch (aggregation.toUpperCase()) {
      case 'SUM':
        return values.reduce((sum, val) => sum + val, 0);
      case 'AVG':
      case 'AVERAGE':
        return values.reduce((sum, val) => sum + val, 0) / values.length;
      case 'MIN':
        return Math.min(...values);
      case 'MAX':
        return Math.max(...values);
      case 'COUNT':
        return values.length;
      default:
        return values.reduce((sum, val) => sum + val, 0);
    }
  };

  // Transform chart data to match ChartDisplay expected format
  const transformChartForDisplay = (chart) => {
    console.log('WidgetBuilder - Transforming chart for display:', chart);

    // Convert underscore field names to camelCase for ChartDisplay compatibility
    const transformedChart = {
      ...chart,
      // Convert chart_type to chartType
      chartType: chart.chart_type || chart.chartType,
      // Convert field names to camelCase
      xAxisField: chart.x_axis_field || chart.xAxisField,
      yAxisField: chart.y_axis_field || chart.yAxisField,
      yAxisFields: chart.y_axis_fields || chart.yAxisFields,
      groupField: chart.group_field || chart.groupField,
      pieValueField: chart.pie_value_field || chart.pieValueField,
      pieSliceField: chart.pie_slice_field || chart.pieSliceField,
      // Scatter chart fields
      scatterXAxis: chart.scatter_x_axis || chart.scatterXAxis,
      scatterYAxis: chart.scatter_y_axis || chart.scatterYAxis,
      scatterGroupField: chart.scatter_group_field || chart.scatterGroupField,
      scatterXAxisLabel: chart.scatter_x_axis_label || chart.scatterXAxisLabel,
      scatterYAxisLabel: chart.scatter_y_axis_label || chart.scatterYAxisLabel,
      // Other fields
      legendPosition: chart.legend_position || chart.legendPosition || 'top',
      showTooltips: chart.show_tooltips !== undefined ? chart.show_tooltips : (chart.showTooltips !== undefined ? chart.showTooltips : true),
      showLabels: chart.show_labels !== undefined ? chart.show_labels : (chart.showLabels !== undefined ? chart.showLabels : true),
      labelColor: chart.label_color || chart.labelColor,
      xAxisLabel: chart.x_axis_label || chart.xAxisLabel,
      yAxisLabel: chart.y_axis_label || chart.yAxisLabel,
      // Field colors
      fieldColors: chart.field_colors || chart.fieldColors
    };

    console.log('WidgetBuilder - Transformed chart:', transformedChart);
    return transformedChart;
  };

  const handleChartSelect = async (chart) => {
    console.log('WidgetBuilder - Chart selected:', chart);
    console.log('WidgetBuilder - Current selectedFields:', selectedFields);
    console.log('WidgetBuilder - Current summarizedFields:', summarizedFields);
    console.log('WidgetBuilder - Current selectedGroups:', selectedGroups);

    setSelectedChart(chart);

    const chartConfig = {
      ...chart,
      // Store complete chart configuration with proper property mapping
      chartType: chart.chartType || chart.chart_type,
      title: chart.title,
      xAxisField: chart.xAxisField || chart.x_axis_field,
      yAxisField: chart.yAxisField || chart.y_axis_field,
      yAxisFields: chart.yAxisFields || chart.y_axis_fields,
      groupField: chart.groupField || chart.group_field,
      pieValueField: chart.pieValueField || chart.pie_value_field,
      pieSliceField: chart.pieSliceField || chart.pie_slice_field,
      scatterXAxis: chart.scatterXAxis || chart.scatter_x_axis,
      scatterYAxis: chart.scatterYAxis || chart.scatter_y_axis,
      scatterGroupField: chart.scatterGroupField || chart.scatter_group_field,
      scatterXAxisLabel: chart.scatterXAxisLabel || chart.scatter_x_axis_label,
      scatterYAxisLabel: chart.scatterYAxisLabel || chart.scatter_y_axis_label,
      legendPosition: chart.legendPosition || chart.legend_position,
      showTooltips: chart.showTooltips !== undefined ? chart.showTooltips : chart.show_tooltips,
      showLabels: chart.showLabels !== undefined ? chart.showLabels : chart.show_labels,
      labelColor: chart.labelColor || chart.label_color,
      xAxisLabel: chart.xAxisLabel || chart.x_axis_label,
      yAxisLabel: chart.yAxisLabel || chart.y_axis_label,
      fieldColors: chart.fieldColors || chart.field_colors,
      // Preserve all other chart properties
      id: chart.id,
      createdby: chart.createdby,
      createdbyid: chart.createdbyid,
      createddate: chart.createddate,
      lastmodifiedby: chart.lastmodifiedby,
      lastmodifiedbyid: chart.lastmodifiedbyid,
      lastmodifieddate: chart.lastmodifieddate
    };

    console.log('WidgetBuilder - Chart configuration being set:', {
      originalChart: chart,
      processedChartConfig: chartConfig,
      chartType: chartConfig.chartType,
      scatterXAxis: chartConfig.scatterXAxis,
      scatterYAxis: chartConfig.scatterYAxis,
      scatterGroupField: chartConfig.scatterGroupField
    });

    setWidgetConfig(prev => ({
      ...prev,
      chart_type: chart.chartType || chart.chart_type,
      chart_config: chartConfig
    }));

    // Only auto-advance to step 4 if we're not in step 3 for report or task widgets
    // For report/task widgets in step 3, user should click "Next" after selecting chart or full report
    if (!(currentStep === 3 && (widgetType === 'report' || widgetType === 'task'))) {
      setCurrentStep(4);
    }
  };

  const handleConfigChange = (field, value) => {
    setWidgetConfig(prev => ({ ...prev, [field]: value }));
  };

  const handlePreview = async () => {
    try {
      setLoading(true);

      // Set default widget name if not provided
      let defaultName;
      if (widgetType === 'dashboard') {
        defaultName = selectedDashboard?.name || 'Dashboard Widget';
      } else if (widgetType === 'report') {
        defaultName = includeFullReport
          ? `${selectedReport?.report_label || 'Report'} - Report Viewer`
          : selectedChart?.title || `${selectedReport?.report_label || 'Report'} - ${getChartTypeLabel(selectedChart?.chart_type || widgetConfig.chart_type)}`;
      } else {
        defaultName = selectedChart?.title || `${selectedReport?.name || 'Report'} - ${getChartTypeLabel(selectedChart?.chart_type || widgetConfig.chart_type)}`;
      }

      console.log('## Selected Chart:', selectedChart);
      console.log('## Selected Dashboard:', selectedDashboard);

      // Create widget preview data (without saving to backend)
      const isFullReportWidget = widgetType === 'report' && includeFullReport;
      const isReportChartWidget = widgetType === 'report' && !includeFullReport;

      const isDashboardWidgetType = widgetType === 'dashboard';

      const widgetPreviewData = {
        id: editingWidget ? editingWidget.id : `preview-${Date.now()}`, // Keep existing ID for editing
        name: widgetConfig.name || defaultName,
        widget_type: isFullReportWidget ? 'report' : (widgetConfig.widget_type || (widgetType === 'report' ? 'chart' : widgetType)),
        type: isFullReportWidget ? 'report' : isReportChartWidget ? 'chart' : (widgetConfig.widget_type || widgetType),
        chart_type: isFullReportWidget
          ? 'report'
          : widgetType === 'dashboard'
            ? 'dashboard'
            : (selectedChart?.chartType || widgetConfig.chart_type),
        column_span: widgetConfig.column_span || 6,
        row_span: widgetConfig.row_span || 4,
        refresh_rate: widgetConfig.refresh_rate,
        show_filters: widgetConfig.show_filters,
        show_export: widgetConfig.show_export,
        show_header: isDashboardWidgetType ? widgetConfig.show_header : true,
        sort_order: widgetConfig.sort_order,
        chart_config: isReportChartWidget ? (widgetConfig.chart_config || selectedChart || {}) : (isFullReportWidget ? null : widgetConfig.chart_config || selectedChart || {}),
        dashboard_id: widgetConfig.dashboard_id,
        task_id: widgetConfig.task_id,
        report_id: selectedReport?.id,
        report_label: widgetConfig.report_label || selectedReport?.report_label, // Save report label
        chart_id: isReportChartWidget ? selectedChart?.id : null,
        isPreview: !editingWidget, // Only new widgets are previews
        x: editingWidget ? editingWidget.x : 0,
        y: editingWidget ? editingWidget.y : 0,
        previewData: editingWidget ? (editingWidget.previewData || previewData) : previewData,
        summarizedData: editingWidget ? (editingWidget.summarizedData || editingWidget.dataset || summarizedData) : summarizedData,
        groupingInfo: editingWidget ? (editingWidget.groupingInfo || groupingInfo) : groupingInfo,
        selectedFields: editingWidget ? (editingWidget.selectedFields || selectedFields) : selectedFields,
        summarizedFields: editingWidget ? (editingWidget.summarizedFields || summarizedFields) : summarizedFields,
        selectedGroups: editingWidget ? (editingWidget.selectedGroups || selectedGroups) : selectedGroups,
        selectedChart: isReportChartWidget ? selectedChart : null,
        selectedDashboard: selectedDashboard,
        dataset: editingWidget ? editingWidget.dataset : undefined
      };

      if (isFullReportWidget) {
        widgetPreviewData.chart_config = null;
        widgetPreviewData.chart_id = null;
      }

      console.log('WidgetBuilder - Creating/updating widget:', widgetPreviewData);
      console.log('WidgetBuilder - Chart configuration being passed:', {
        selectedFields: selectedFields,
        summarizedFields: summarizedFields,
        selectedGroups: selectedGroups,
        previewData: previewData,
        summarizedData: summarizedData,
        selectedChart: selectedChart,
        widgetConfigChartConfig: widgetConfig.chart_config,
        isEditing: !!editingWidget
      });

      // Call the preview function
      if (onPreviewWidget) {
        await onPreviewWidget(widgetPreviewData);
      }

      // Close the builder
      onClose();

    } catch (error) {
      console.error('WidgetBuilder - Error creating/updating widget:', error);
      setError('Failed to create/update widget');
    } finally {
      setLoading(false);
    }
  };


  const getChartIcon = (chartType) => {
    switch (chartType) {
      case 'bar':
        return <FaChartBar />;
      case 'line':
        return <FaChartLine />;
      case 'pie':
        return <FaChartPie />;
      case 'area':
        return <FaChartArea />;
      case 'table':
        return <FaTable />;
      default:
        return <FaChartBar />;
    }
  };

  const getChartTypeLabel = (chartType) => {
    switch (chartType) {
      case 'bar':
        return 'Bar Chart';
      case 'line':
        return 'Line Chart';
      case 'donut':
        return 'Pie Chart';
      case 'area':
        return 'Area Chart';
      case 'table':
        return 'Table';
      default:
        return 'Chart';
    }
  };

  if (!isOpen) return null;

  // Get summarized data for charts (same as ReportDataViewer)
  const summarizedData = createSummarizedDataForCharts() || {};
  const shouldShowNextButton = currentStep === 3 && (widgetType === 'report' || widgetType === 'task');
  const isNextDisabled =
    shouldShowNextButton &&
    (
      (widgetType === 'report' && !includeFullReport && !selectedChart) ||
      (widgetType === 'task' && !selectedChart)
    );

  const renderReportOptions = () => {
    const showFullReportToggle = widgetType === 'report';
    const isShowingCharts = !showFullReportToggle || !includeFullReport;

    return (
      <div className="home-widget-report-options">
        {showFullReportToggle && (
          <div className="home-widget-report-mode-toggle">
            <button
              type="button"
              className={`home-widget-report-mode-btn ${includeFullReport ? 'active' : ''}`}
              onClick={() => handleIncludeFullReportToggle(true)}
            >
              <span className="home-widget-report-mode-title">Full Report</span>
              <span className="home-widget-report-mode-subtitle">Embed complete report viewer</span>
            </button>
            <button
              type="button"
              className={`home-widget-report-mode-btn ${!includeFullReport ? 'active' : ''}`}
              onClick={() => handleIncludeFullReportToggle(false)}
            >
              <span className="home-widget-report-mode-title">Specific Chart</span>
              <span className="home-widget-report-mode-subtitle">Pick from report charts</span>
            </button>
          </div>
        )}

        {showFullReportToggle && includeFullReport && (
          <p className="home-widget-form-help-text">
            The widget will render the entire report using the standard report viewer. Sort order and sizing can be adjusted in the next step.
          </p>
        )}

        {isShowingCharts && (
          <div className="home-widget-chart-options">
            <div className="existing-charts">
              <h4>Existing Charts</h4>
              {reportCharts && reportCharts.length > 0 ? (
                <div className="charts-list">
                  {reportCharts.map((chart) => {
                    const chartId = chart.id || chart.chart_id || chart.tempId;
                    const isSelected = selectedChart?.id === chartId || selectedChart?.id === chart.id;
                    const chartTypeValue = chart.chart_type || chart.chartType || 'chart';

                    return (
                      <div
                        key={chartId || `${chart.title}-${chartTypeValue}`}
                        className={`chart-preview-item ${isSelected ? 'selected' : ''}`}
                        onClick={() => handleChartSelect(chart)}
                      >
                        <div className="chart-preview-header">
                          <div className="chart-icon">
                            {getChartIcon(chartTypeValue)}
                          </div>
                          <div className="chart-info">
                            <h5>{chart.title || 'Untitled Chart'}</h5>
                            <p>{getChartTypeLabel(chartTypeValue)}</p>
                          </div>
                          <div className="chart-select-btn">
                            <FaArrowRight />
                          </div>
                        </div>
                        <div className="chart-preview-content">
                          {previewLoading ? (
                            <div className="chart-preview-placeholder">
                              <div className="placeholder-icon">
                                {getChartIcon(chartTypeValue)}
                              </div>
                              <p>Loading chart preview...</p>
                              <small>{chart.title || 'Untitled Chart'}</small>
                            </div>
                          ) : previewData.length > 0 && summarizedData && Object.keys(summarizedData).length > 0 ? (
                            <div className="chart-preview">
                              <ChartDisplay
                                chart={transformChartForDisplay(chart)}
                                selectedFields={selectedFields}
                                summarizedFields={summarizedFields}
                                selectedGroups={selectedGroups}
                                summarizedData={summarizedData}
                                previewData={previewData}
                                isPreview={true}
                                displayAuditInfo={false}
                              />
                            </div>
                          ) : (
                            <div className="chart-preview-placeholder">
                              <div className="placeholder-icon">
                                {getChartIcon(chartTypeValue)}
                              </div>
                              <p>Chart Preview</p>
                              <small>{chart.title || 'Untitled Chart'}</small>
                              <div className="chart-config-info">
                                <div className="config-item">
                                  <strong>Type:</strong> {getChartTypeLabel(chartTypeValue)}
                                </div>
                                {(chart.x_axis_field || chart.xAxisField) && (
                                  <div className="config-item">
                                    <strong>X-Axis:</strong> {chart.x_axis_field || chart.xAxisField}
                                  </div>
                                )}
                                {(chart.y_axis_field || chart.yAxisField) && (
                                  <div className="config-item">
                                    <strong>Y-Axis:</strong> {chart.y_axis_field || chart.yAxisField}
                                  </div>
                                )}
                                {(chart.group_field || chart.groupField) && (
                                  <div className="config-item">
                                    <strong>Group By:</strong> {chart.group_field || chart.groupField}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="no-charts">
                  <p>No existing charts found for this report.</p>
                </div>
              )}
            </div>

          </div>
        )}
      </div>
    );
  };

  return (
    <div className="widget-builder-overlay">
      <div className="widget-builder">
        {/* Header */}
        <div className="builder-header">
          <div className="builder-title">
            <h2>{editingWidget ? 'Edit Widget' : 'Add Widget'}</h2>
            <button className="builder-close-btn" onClick={onClose}>×</button>
          </div>
          <div className="builder-steps">
            <div className={`step ${currentStep >= 1 ? 'active' : ''}`}>
              <span className="step-number">1</span>
              <span className="step-label">Type</span>
            </div>
            <div className={`step ${currentStep >= 2 ? 'active' : ''}`}>
              <span className="step-number">2</span>
              <span className="step-label">{widgetType === 'dashboard' ? 'Dashboard' : widgetType === 'report' ? 'Report' : widgetType === 'task' ? 'Task' : 'Select'}</span>
            </div>
            {widgetType === 'report' && (
              <div className={`step ${currentStep >= 3 ? 'active' : ''}`}>
                <span className="step-number">3</span>
                <span className="step-label">Chart</span>
              </div>
            )}
            {widgetType === 'report' && (
              <div className={`step ${currentStep >= 4 ? 'active' : ''}`}>
                <span className="step-number">4</span>
                <span className="step-label">Config</span>
              </div>
            )}
            {widgetType === 'task' && (
              <div className={`step ${currentStep >= 3 ? 'active' : ''}`}>
                <span className="step-number">3</span>
                <span className="step-label">Config</span>
              </div>
            )}
            {widgetType === 'dashboard' && (
              <div className={`step ${currentStep >= 3 ? 'active' : ''}`}>
                <span className="step-number">3</span>
                <span className="step-label">Config</span>
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="builder-content">
          {error && (
            <div className="builder-error">
              <p>{error}</p>
              <button onClick={() => setError(null)}>×</button>
            </div>
          )}

          {/* Step 1: Choose Widget Type */}
          {currentStep === 1 && (
            <div className="builder-step">
              <div className="step-header">
                <h3>Choose Widget Type</h3>
                <p>Select the type of widget you want to add</p>
              </div>

              <div className="type-selection">
                <div className="type-options">
                  <div
                    className="type-option"
                    onClick={() => handleTypeSelect('dashboard')}
                  >
                    <div className="type-icon">
                      <FaTachometerAlt />
                    </div>
                    <div className="type-info">
                      <h4>Dashboard Widget</h4>
                      <p>Add a dashboard as a widget to your home screen</p>
                    </div>
                    <div className="type-arrow">
                      <FaArrowRight />
                    </div>
                  </div>

                  <div
                    className="type-option"
                    onClick={() => handleTypeSelect('report')}
                  >
                    <div className="type-icon">
                      <FaChartBar />
                    </div>
                    <div className="type-info">
                      <h4>Report Widget</h4>
                      <p>Add a report or chart as a widget to your home screen</p>
                    </div>
                    <div className="type-arrow">
                      <FaArrowRight />
                    </div>
                  </div>

                  <div
                    className="type-option"
                    onClick={() => handleTypeSelect('task')}
                  >
                    <div className="type-icon">
                      <FaTasks />
                    </div>
                    <div className="type-info">
                      <h4>Task Widget</h4>
                      <p>Add Task as a widget</p>
                    </div>
                    <div className="type-arrow">
                      <FaArrowRight />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Choose Report */}
          {currentStep === 2 && widgetType === 'report' && (
            <div className="builder-step">
              <div className="step-header">
                <button
                  className="back-btn"
                  onClick={() => setCurrentStep(1)}
                >
                  <FaArrowLeft />
                  Back
                </button>
                <h3>Choose a Report</h3>
                <p>Select the report that will be displayed as a widget</p>
              </div>

              <div className="search-section">
                <div className="search-box">
                  <FaSearch className="search-icon" />
                  <input
                    type="text"
                    placeholder="Search reports..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              <div className="reports-list">
                {loading ? (
                  <div className="loading-state">
                    <div className="loading-spinner"></div>
                    <p>Loading reports...</p>
                  </div>
                ) : filteredReports.length === 0 ? (
                  <div className="empty-state">
                    <p>No reports found</p>
                  </div>
                ) : (
                  filteredReports.map((report) => (
                    <div
                      key={report.id}
                      className="report-item"
                      onClick={() => handleReportSelect(report)}
                    >
                      <div className="report-icon">
                        <FaChartBar />
                      </div>
                      <div className="report-info">
                        <h4 className="report-name">{report.report_label}</h4>
                        {report.description && (
                          <p className="report-description">{report.description}</p>
                        )}
                        <div className="report-meta">
                          <span className="report-type">Report</span>
                        </div>
                      </div>
                      <div className="report-arrow">
                        <FaArrowRight />
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Step 2: Choose Dashboard or Task */}
          {currentStep === 2 && widgetType === 'dashboard' && (
            <div className="builder-step">
              <div className="step-header">
                <button
                  className="back-btn"
                  onClick={() => setCurrentStep(1)}
                >
                  <FaArrowLeft />
                  Back
                </button>
                <h3>
                  {widgetType === 'dashboard' ? 'Choose a Dashboard' : 'Choose a Task'}
                </h3>
                <p>
                  {widgetType === 'dashboard'
                    ? 'Select the dashboard that will be displayed as a widget'
                    : 'Select the report that will provide data for your task widget'
                  }
                </p>
              </div>

              <div className="search-section">
                <div className="search-box">
                  <FaSearch className="search-icon" />
                  <input
                    type="text"
                    placeholder={`Search ${widgetType === 'dashboard' ? 'dashboards' : 'reports'}...`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              {widgetType === 'dashboard' ? (
                <div className="dashboards-list">
                  {loading ? (
                    <div className="loading-state">
                      <div className="loading-spinner"></div>
                      <p>Loading dashboards...</p>
                    </div>
                  ) : filteredDashboards.length === 0 ? (
                    <div className="empty-state">
                      <p>No dashboards found</p>
                    </div>
                  ) : (
                    filteredDashboards.map((dashboard) => (
                      <div
                        key={dashboard.id}
                        className="dashboard-item"
                        onClick={() => handleDashboardSelect(dashboard)}
                      >
                        <div className="dashboard-icon">
                          <FaTachometerAlt />
                        </div>
                        <div className="dashboard-info">
                          <h4 className="dashboard-name">{dashboard.name}</h4>
                          {dashboard.description && (
                            <p className="dashboard-description">{dashboard.description}</p>
                          )}
                          <div className="dashboard-meta">
                            <span className="dashboard-type">Dashboard</span>
                            <span className="dashboard-date">
                              Created: {new Date(dashboard.createddate).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <div className="dashboard-arrow">
                          <FaArrowRight />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              ) : (
                <div className="reports-list">
                  {loading ? (
                    <div className="loading-state">
                      <div className="loading-spinner"></div>
                      <p>Loading reports...</p>
                    </div>
                  ) : filteredReports.length === 0 ? (
                    <div className="empty-state">
                      <p>No reports found</p>
                    </div>
                  ) : (
                    filteredReports.map((report) => (
                      <div
                        key={report.id}
                        className="report-item"
                        onClick={() => handleReportSelect(report)}
                      >
                        <div className="report-icon">
                          <FaChartBar />
                        </div>
                        <div className="report-info">
                          <h4 className="report-name">{report.report_label}</h4>
                          {report.description && (
                            <p className="report-description">{report.description}</p>
                          )}
                          <div className="report-meta">
                            <span className="report-type">Report</span>
                            <span className="report-date">
                              Created: {new Date(report.createddate).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <div className="report-arrow">
                          <FaArrowRight />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 2: Select Report (for tasks) */}
          {currentStep === 2 && widgetType === 'task' && (
            <div className="builder-step">
              <div className="step-header">
                <button
                  className="back-btn"
                  onClick={() => setCurrentStep(1)}
                >
                  <FaArrowLeft />
                  Back
                </button>
                <h3>Select Report</h3>
                <p>Choose a report to create a widget from</p>
              </div>

              <div className="search-section">
                <div className="search-box">
                  <FaSearch className="search-icon" />
                  <input
                    type="text"
                    placeholder="Search reports..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              <div className="reports-list">
                {loading ? (
                  <div className="loading-state">
                    <div className="loading-spinner"></div>
                    <p>Loading reports...</p>
                  </div>
                ) : filteredReports.length === 0 ? (
                  <div className="empty-state">
                    <p>No reports found</p>
                  </div>
                ) : (
                  filteredReports.map((report) => (
                    <div
                      key={report.id}
                      className="report-item"
                      onClick={() => handleReportSelect(report)}
                    >
                      <div className="report-icon">
                        <FaChartBar />
                      </div>
                      <div className="report-info">
                        <h4 className="report-name">{report.report_label}</h4>
                        {report.description && (
                          <p className="report-description">{report.description}</p>
                        )}
                        <div className="report-meta">
                          <span className="report-type">Report</span>
                          <span className="report-date">
                            Created: {new Date(report.createddate).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <div className="report-arrow">
                        <FaArrowRight />
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Step 3: Choose Chart (for tasks) */}
          {currentStep === 3 && widgetType === 'task' && (
            <div className="builder-step">
              <div className="step-header">
                {!editingWidget && (
                  <button
                    className="back-btn"
                    onClick={() => setCurrentStep(2)}
                  >
                    <FaArrowLeft />
                    Back
                  </button>
                )}
                <h3>Choose Chart Type</h3>
                <p>Select an existing chart to display in your widget</p>
              </div>

              {selectedReport && (
                <div className="selected-report">
                  <div className="report-summary">
                    <FaChartBar className="report-icon" />
                    <div>
                      <h4>{selectedReport.report_label}</h4>
                      <p>Selected Report</p>
                    </div>
                  </div>
                </div>
              )}

              {renderReportOptions()}

            </div>
          )}

          {/* Step 3: Choose Chart (for reports) */}
          {currentStep === 3 && widgetType === 'report' && (
            <div className="builder-step">
              <div className="step-header">
                <button
                  className="back-btn"
                  onClick={() => setCurrentStep(2)}
                >
                  <FaArrowLeft />
                  Back
                </button>
                <h3>Choose Chart Type</h3>
                <p>Select an existing chart to display in your widget</p>
              </div>

              {selectedReport && (
                <div className="selected-report">
                  <div className="report-summary">
                    <FaChartBar className="report-icon" />
                    <div>
                      <h4>{selectedReport.report_label}</h4>
                      <p>Selected Report</p>
                    </div>
                  </div>
                </div>
              )}

              {renderReportOptions()}

            </div>
          )}

          {/* Step 4: Widget Configuration (for reports) */}
          {currentStep === 4 && widgetType === 'report' && (
            <div className="builder-step">
              <div className="step-header">
                {!editingWidget && (
                  <button
                    className="back-btn"
                    onClick={() => setCurrentStep(3)}
                  >
                    <FaArrowLeft />
                    Back
                  </button>
                )}
                <h3>Configure Report Widget</h3>
                <p>Set up your report widget display and behavior</p>
              </div>

              <div className="config-form">
                <div className="form-group">
                  <label htmlFor="widget-name">Widget Name</label>
                  <input
                    type="text"
                    id="widget-name"
                    value={widgetConfig.name}
                    onChange={(e) => handleConfigChange('name', e.target.value)}
                    placeholder="Enter widget name"
                    className="form-input"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="column-span">Width</label>
                    <select
                      id="column-span"
                      value={widgetConfig.column_span}
                      onChange={(e) => handleConfigChange('column_span', parseInt(e.target.value))}
                    >
                      <option value={3}>3 Columns (25%)</option>
                      <option value={4}>4 Columns (33%)</option>
                      <option value={6}>6 Columns (50%)</option>
                      <option value={8}>8 Columns (67%)</option>
                      <option value={9}>9 Columns (75%)</option>
                      <option value={12}>12 Columns (Full Width)</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="row-span">Rows (Height)</label>
                    <select
                      id="row-span"
                      value={widgetConfig.row_span}
                      onChange={(e) => handleConfigChange('row_span', parseInt(e.target.value))}
                    >
                      {rowOptions.map((value) => (
                        <option key={value} value={value}>
                          {getRowOptionLabel(value)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="sort-order">Sort Order</label>
                  <input
                    type="number"
                    id="sort-order"
                    value={widgetConfig.sort_order}
                    onChange={(e) => handleConfigChange('sort_order', parseInt(e.target.value) || 0)}
                    placeholder="Enter sort order"
                    className="form-input"
                  />
                </div>

                <div className="config-actions">
                  <button
                    className="add-widget-btn"
                    onClick={handlePreview}
                  >
                    <FaPlus />
                    {editingWidget ? 'Update Widget' : 'Add Widget'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Widget Configuration (for dashboards) */}
          {currentStep === 3 && widgetType === 'dashboard' && (
            <div className="builder-step">
              <div className="step-header">
                <button
                  className="back-btn"
                  onClick={() => setCurrentStep(2)}
                >
                  <FaArrowLeft />
                  Back
                </button>
                <h3>Configure Dashboard Widget</h3>
                <p>Set the widget size and display options</p>
              </div>

              {selectedDashboard && (
                <div className="selected-dashboard">
                  <div className="dashboard-summary">
                    <FaTachometerAlt className="dashboard-icon" />
                    <div>
                      <h4>{selectedDashboard.name}</h4>
                      <p>Selected Dashboard</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="widget-config-form">
                <div className="config-section">
                  <h4>Widget Settings</h4>

                  <div className="form-group">
                    <label htmlFor="widget-name">Widget Name</label>
                    <input
                      id="widget-name"
                      type="text"
                      value={widgetConfig.name}
                      onChange={(e) => handleConfigChange('name', e.target.value)}
                      placeholder="Enter widget name..."
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="column-span">Columns (Width)</label>
                    <select
                      id="column-span"
                      value={widgetConfig.column_span}
                      onChange={(e) => handleConfigChange('column_span', parseInt(e.target.value))}
                    >
                      <option value={1}>1 Column</option>
                      <option value={2}>2 Columns</option>
                      <option value={3}>3 Columns</option>
                      <option value={4}>4 Columns</option>
                      <option value={5}>5 Columns</option>
                      <option value={6}>6 Columns (Default)</option>
                      <option value={7}>7 Columns</option>
                      <option value={8}>8 Columns</option>
                      <option value={9}>9 Columns</option>
                      <option value={10}>10 Columns</option>
                      <option value={11}>11 Columns</option>
                      <option value={12}>12 Columns (Full Width)</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="row-span">Rows (Height)</label>
                    <select
                      id="row-span"
                      value={widgetConfig.row_span}
                      onChange={(e) => handleConfigChange('row_span', parseInt(e.target.value))}
                    >
                      {rowOptions.map((value) => (
                        <option key={value} value={value}>
                          {getRowOptionLabel(value)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="sort-order">Sort Order</label>
                    <input
                      id="sort-order"
                      type="number"
                      min="1"
                      value={widgetConfig.sort_order || ''}
                      onChange={(e) => handleConfigChange('sort_order', parseInt(e.target.value) || '')}
                      placeholder="Enter sort order number..."
                    />
                  </div>

                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={widgetConfig.show_header}
                        onChange={(e) => handleConfigChange('show_header', e.target.checked)}
                      />
                      <span className="checkbox-text">Show Header</span>
                    </label>
                    <p className="form-help-text">
                      Display the dashboard header with title and controls
                    </p>
                  </div>
                </div>

                <div className="config-actions">
                  <button
                    className="add-widget-btn"
                    onClick={handlePreview}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <div className="loading-spinner-small"></div>
                        {editingWidget ? 'Updating Widget...' : 'Adding Widget...'}
                      </>
                    ) : (
                      <>
                        <FaPlus />
                        {editingWidget ? 'Update Widget' : 'Add Widget'}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Widget Configuration (for tasks) */}
          {currentStep === 4 && widgetType === 'task' && (
            <div className="builder-step">
              <div className="step-header">
                {!editingWidget && (
                  <button
                    className="back-btn"
                    onClick={() => setCurrentStep(2)}
                  >
                    <FaArrowLeft />
                    Back
                  </button>
                )}
                <h3>{editingWidget ? 'Edit Widget' : 'Configure Widget'}</h3>
                <p>{editingWidget ? 'Modify the widget settings' : 'Set the widget size, name, and display options'}</p>
              </div>

              {selectedChart && selectedChart.chart_type && (
                <div className="selected-chart">
                  <div className="chart-summary">
                    <div className="chart-icon">
                      {selectedChart.chartType === 'bar' && <FaChartBar />}
                      {selectedChart.chartType === 'line' && <FaChartLine />}
                      {selectedChart.chartType === 'pie' && <FaChartPie />}
                      {selectedChart.chartType === 'area' && <FaChartArea />}
                      {selectedChart.chartType === 'table' && <FaTable />}
                    </div>
                    <div>
                      <h4>{selectedChart.name || `${selectedChart.chartType.charAt(0).toUpperCase() + selectedChart.chartType.slice(1)} Chart`}</h4>
                      <p>Selected Chart</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="widget-config-form">
                <div className="config-section">
                  <h4>Widget Settings</h4>

                  <div className="form-group">
                    <label htmlFor="widget-name">Widget Name</label>
                    <input
                      id="widget-name"
                      type="text"
                      value={widgetConfig.name}
                      onChange={(e) => handleConfigChange('name', e.target.value)}
                      placeholder="Enter widget name..."
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="column-span">Columns (Width)</label>
                    <select
                      id="column-span"
                      value={widgetConfig.column_span}
                      onChange={(e) => handleConfigChange('column_span', parseInt(e.target.value))}
                    >
                      <option value={1}>1 Column</option>
                      <option value={2}>2 Columns</option>
                      <option value={3}>3 Columns</option>
                      <option value={4}>4 Columns</option>
                      <option value={5}>5 Columns</option>
                      <option value={6}>6 Columns (Default)</option>
                      <option value={7}>7 Columns</option>
                      <option value={8}>8 Columns</option>
                      <option value={9}>9 Columns</option>
                      <option value={10}>10 Columns</option>
                      <option value={11}>11 Columns</option>
                      <option value={12}>12 Columns (Full Width)</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="row-span">Rows (Height)</label>
                    <select
                      id="row-span"
                      value={widgetConfig.row_span}
                      onChange={(e) => handleConfigChange('row_span', parseInt(e.target.value))}
                    >
                      {rowOptions.map((value) => (
                        <option key={value} value={value}>
                          {getRowOptionLabel(value)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="sort-order">Sort Order</label>
                    <input
                      id="sort-order"
                      type="number"
                      min="1"
                      value={widgetConfig.sort_order || ''}
                      onChange={(e) => handleConfigChange('sort_order', parseInt(e.target.value) || '')}
                      placeholder="Enter sort order number..."
                    />
                  </div>
                </div>

                <div className="config-actions">
                  <button
                    className="add-widget-btn"
                    onClick={handlePreview}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <div className="loading-spinner-small"></div>
                        {editingWidget ? 'Updating Widget...' : 'Adding Widget...'}
                      </>
                    ) : (
                      <>
                        <FaPlus />
                        {editingWidget ? 'Update Widget' : 'Add Widget'}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="builder-footer">
          <div className="footer-actions">
            <div className="footer-buttons">
              <button
                className="cancel-btn"
                onClick={onClose}
              >
                Cancel
              </button>
              {shouldShowNextButton && (
                <button
                  className="next-btn"
                  onClick={() => setCurrentStep(4)}
                  disabled={isNextDisabled}
                >
                  Next
                  <FaArrowRight />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeWidgetBuilder;
